import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { apiFetch } from '../lib/api';
import { Tag, User, Phone, MapPin, ShoppingBag, DollarSign, Calendar, Search, X, ChevronDown, ChevronUp, AlertCircle, Edit, Trash2, Download, FileUp, FileJson, Loader2 } from 'lucide-react';
import { formatPrice } from '../lib/utils';
import TagManager from '../components/TagManager';

interface Customer {
  phone: string;
  name: string;
  address: string;
  estate: string | null;
  orderCount: number;
  totalSpent: number;
  lastOrderAt: number;
  lastOrderId: number;
  manualTags: ManualTag[];
  source?: 'order' | 'imported';
  importedAt?: number | string;
  isUnsubscribed?: boolean;
  unsubscribedAt?: number | string;
  unsubscribeRemark?: string;
}

interface ManualTag {
  id: number;
  tag: string;
  color: string;
}

interface AutoTag {
  key: string;
  label: string;
  color: string;
  explanation?: string;
}

interface AutoTagRule {
  key: string;
  label: string;
  color: string;
  conditions: { field: string; operator: string; value: unknown }[];
  explanation?: string;
}

const TAG_COLORS: Record<string, string> = {
  gray: 'bg-gray-100 text-gray-700 border-gray-200',
  red: 'bg-red-100 text-red-700 border-red-200',
  orange: 'bg-red-100 text-red-700 border-red-200',
  yellow: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  green: 'bg-green-100 text-green-700 border-green-200',
  teal: 'bg-teal-100 text-teal-700 border-teal-200',
  blue: 'bg-blue-100 text-blue-700 border-blue-200',
  purple: 'bg-purple-100 text-purple-700 border-purple-200',
  pink: 'bg-pink-100 text-pink-700 border-pink-200',
};

const evaluateAutoTags = (customer: Customer, rules: AutoTagRule[]): AutoTag[] => {
  const matched: AutoTag[] = [];
  for (const rule of rules) {
    const allMatch = (rule.conditions || []).every((cond) => {
      if (cond.field === 'hasManualTag') {
        return customer.manualTags.some((t) => t.tag === String(cond.value));
      }
      let actual: number | string | boolean | null = null;
      if (cond.field === 'daysSinceLastOrder') {
        const last = customer.lastOrderAt || 0;
        if (!last) return false;
        actual = Math.floor((Date.now() / 1000 - last) / 86400);
      } else if (cond.field === 'isUnsubscribed') {
        actual = !!customer.isUnsubscribed;
      } else if (cond.field === 'source') {
        actual = String(customer.source || '');
      } else if (cond.field === 'orderCount') {
        actual = customer.orderCount || 0;
      } else if (cond.field === 'totalSpent') {
        actual = customer.totalSpent || 0;
      } else if (cond.field === 'lastOrderAt') {
        actual = customer.lastOrderAt || 0;
      } else {
        actual = String((customer as any)[cond.field] ?? '');
      }
      const target = cond.value;
      const op = cond.operator;
      if (op === 'contains' && typeof actual === 'string' && typeof target === 'string') return actual.includes(target);
      if (op === 'in' && Array.isArray(target)) return target.includes(String(actual));
      const numActual = Number(actual);
      const numTarget = Number(target);
      if (!Number.isNaN(numActual) && !Number.isNaN(numTarget) && typeof target !== 'boolean') {
        switch (op) {
          case 'eq': return numActual === numTarget;
          case 'ne': return numActual !== numTarget;
          case 'gt': return numActual > numTarget;
          case 'gte': return numActual >= numTarget;
          case 'lt': return numActual < numTarget;
          case 'lte': return numActual <= numTarget;
        }
      }
      switch (op) {
        case 'eq': return String(actual) === String(target);
        case 'ne': return String(actual) !== String(target);
        default: return false;
      }
    });
    if (allMatch) {
      matched.push({ key: rule.key, label: rule.label, color: TAG_COLORS[rule.color] || TAG_COLORS.gray, explanation: rule.explanation });
    }
  }
  return matched;
};

const AdminCustomers: React.FC = () => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [allTags, setAllTags] = useState<string[]>([]);
  const [autoTagRules, setAutoTagRules] = useState<AutoTagRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [sortBy, setSortBy] = useState<'lastOrder' | 'orderCount' | 'totalSpent'>('lastOrder');
  const [sortDesc, setSortDesc] = useState(true);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [showTagModal, setShowTagModal] = useState(false);
  const [unsubscribeCustomer, setUnsubscribeCustomer] = useState<Customer | null>(null);
  const [unsubscribeRemark, setUnsubscribeRemark] = useState('');
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [editForm, setEditForm] = useState({ name: '', phone: '', address: '', estate: '' });
  const [isSubmittingEdit, setIsSubmittingEdit] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);

  // Import contact state
  const [showImportModal, setShowImportModal] = useState(false);
  const [importText, setImportText] = useState('');
  const [importFormat, setImportFormat] = useState<'csv' | 'json'>('csv');
  const [importTags, setImportTags] = useState('');
  const [isImporting, setIsImporting] = useState(false);

  const navigate = useNavigate();

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const res = await apiFetch('/api/public/admin/customers');
      if (res.ok) {
        setCustomers(await res.json());
      }
    } catch (e) {
      console.error('Failed to fetch customers:', e);
    }
    setLoading(false);
  };

  const fetchAllTags = async () => {
    try {
      const res = await apiFetch('/api/public/admin/customer-tags/all');
      if (res.ok) {
        setAllTags(await res.json());
      }
    } catch (e) {
      console.error('Failed to fetch tags:', e);
    }
  };

  const fetchAutoTagRules = async () => {
    try {
      const res = await apiFetch('/api/public/admin/auto-tag-rules');
      if (!res.ok) return;
      const data = await res.json();
      const list = Array.isArray(data) ? data : data.data || [];
      setAutoTagRules(list.map((r: any) => ({ ...r, conditions: r.conditions || [] })));
    } catch (e) {
      console.error('Failed to fetch auto tag rules:', e);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin');
      return;
    }
    fetchCustomers();
    fetchAllTags();
    fetchAutoTagRules();
  }, []);

  const parseImport = (): { name: string; phone: string }[] => {
    const lines = importText.trim().split('\n').filter(l => l.trim());
    if (lines.length === 0) return [];
    const results: { name: string; phone: string }[] = [];

    if (importFormat === 'json') {
      try {
        const parsed = JSON.parse(importText);
        const arr = Array.isArray(parsed) ? parsed : parsed.contacts || [];
        return arr.map((c: any) => ({ name: String(c.name || ''), phone: String(c.phone || '') })).filter((c: any) => c.phone);
      } catch { return []; }
    }

    let startIdx = 0;
    const first = lines[0].toLowerCase();
    if (first.includes('name') || first.includes('phone') || first.includes('姓名') || first.includes('電話')) {
      startIdx = 1;
    }
    for (let i = startIdx; i < lines.length; i++) {
      const parts = lines[i].split(',').map(s => s.trim());
      if (parts.length >= 2) {
        results.push({ name: parts[0], phone: parts[1] });
      } else if (parts.length === 1 && parts[0]) {
        results.push({ name: '', phone: parts[0] });
      }
    }
    return results;
  };

  const handleImport = async () => {
    const contacts = parseImport();
    if (contacts.length === 0) {
      alert('無法解析聯繫人，請檢查格式');
      return;
    }
    setIsImporting(true);
    try {
      const tags = importTags.split(',').map(t => t.trim()).filter(Boolean);
      const res = await apiFetch('/api/public/admin/imported-contacts', {
        method: 'POST',
        body: JSON.stringify({
          contacts,
          source: importFormat === 'csv' ? 'csv_upload' : 'json_paste',
          tags
        })
      });
      const data = await res.json();
      if (data.success) {
        setSuccess(`導入成功：新增 ${data.inserted} 個，跳過 ${data.skipped} 個${tags.length > 0 ? `，已打上標籤：${tags.join('、')}` : ''}`);
        setShowImportModal(false);
        setImportText('');
        setImportTags('');
        fetchCustomers();
        fetchAllTags();
        setTimeout(() => setSuccess(null), 5000);
      } else {
        alert(data.error || '導入失敗');
      }
    } catch (e: any) {
      alert(e.message || '導入失敗');
    } finally {
      setIsImporting(false);
    }
  };

  const openEditModal = (customer: Customer) => {
    setEditingCustomer(customer);
    setEditForm({
      name: customer.name || '',
      phone: customer.phone || '',
      address: customer.address || '',
      estate: customer.estate || ''
    });
  };

  const handleEditSave = async () => {
    if (!editingCustomer) return;
    setIsSubmittingEdit(true);
    try {
      const body: any = {
        name: editForm.name,
        address: editForm.address,
        estate: editForm.estate
      };
      if (editingCustomer.source === 'imported' && editForm.phone !== editingCustomer.phone) {
        body.newPhone = editForm.phone;
      }
      const res = await apiFetch(`/api/public/admin/customers/${editingCustomer.phone}`, {
        method: 'PUT',
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (data.success) {
        setEditingCustomer(null);
        fetchCustomers();
      } else {
        alert(data.error || '儲存失敗');
      }
    } catch (e) {
      alert('儲存失敗');
    } finally {
      setIsSubmittingEdit(false);
    }
  };

  const handleDeleteCustomer = async (customer: Customer) => {
    if (customer.source !== 'imported') {
      alert('只有導入聯繫人可以在此刪除；有訂單記錄的客戶無法刪除。');
      return;
    }
    if (!confirm(`確定要刪除 ${customer.name || customer.phone} 嗎？此操作無法復原。`)) return;
    try {
      const res = await apiFetch(`/api/public/admin/customers/${customer.phone}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) {
        fetchCustomers();
      } else {
        alert(data.error || '刪除失敗');
      }
    } catch (e) {
      alert('刪除失敗');
    }
  };

  const downloadCSV = () => {
    const headers = ['姓名', '電話', '來源', '地址', '屋苑', '下單次數', '累計消費', '最後下單', '標籤', '狀態'];
    const rows = sortedCustomers.map(c => {
      const autoTags = evaluateAutoTags(c, autoTagRules).map(t => t.label);
      const manualTags = (c.manualTags || []).map(t => t.tag);
      const allTags = [...autoTags, ...manualTags].join('|');
      return [
        c.name || '',
        c.phone || '',
        c.source === 'imported' ? '導入' : '訂單',
        c.address || '',
        c.estate || '',
        c.orderCount || 0,
        (c.totalSpent / 100).toFixed(0),
        c.lastOrderAt ? new Date(c.lastOrderAt * 1000).toLocaleString('zh-HK') : '',
        allTags,
        c.isUnsubscribed ? '拒收' : '可發送'
      ];
    });
    const csvContent = [headers, ...rows].map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `customers_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleLogout = () => {
    if (confirm('確定要登出嗎？')) {
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
      navigate('/admin');
    }
  };

  const filteredCustomers = customers.filter(c => {
    const term = searchTerm.toLowerCase();
    const matchesSearch =
      !term ||
      (c.name && c.name.toLowerCase().includes(term)) ||
      (c.phone && c.phone.includes(term)) ||
      (c.address && c.address.toLowerCase().includes(term)) ||
      (c.estate && c.estate.toLowerCase().includes(term));

    const autoTags = evaluateAutoTags(c, autoTagRules);
    const matchesTag = !tagFilter ||
      autoTags.some(t => t.key === tagFilter) ||
      c.manualTags.some(t => t.tag === tagFilter);

    return matchesSearch && matchesTag;
  });

  const sortedCustomers = [...filteredCustomers].sort((a, b) => {
    let cmp = 0;
    if (sortBy === 'lastOrder') cmp = a.lastOrderAt - b.lastOrderAt;
    else if (sortBy === 'orderCount') cmp = a.orderCount - b.orderCount;
    else if (sortBy === 'totalSpent') cmp = a.totalSpent - b.totalSpent;
    return sortDesc ? -cmp : cmp;
  });

  const openTagModal = (customer: Customer) => {
    setSelectedCustomer(customer);
    setShowTagModal(true);
  };

  const viewCustomerOrders = (phone: string) => {
    navigate(`/admin/orders?phone=${phone}`);
  };

  const handleUnsubscribe = async () => {
    if (!unsubscribeCustomer) return;
    try {
      const res = await apiFetch(`/api/public/admin/customers/${unsubscribeCustomer.phone}/unsubscribe`, {
        method: 'PUT',
        body: JSON.stringify({
          isUnsubscribed: true,
          remark: unsubscribeRemark.trim() || null,
          addTag: '拒收'
        })
      });
      const data = await res.json();
      if (data.success) {
        setUnsubscribeCustomer(null);
        setUnsubscribeRemark('');
        fetchCustomers();
      } else {
        alert(data.error || '標記失敗');
      }
    } catch (e) {
      alert('標記失敗');
    }
  };

  return (
    <AdminLayout currentPage="customers" onLogout={handleLogout}>
      {/* Header + Filters */}
      <div className="mb-6 bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4 items-end">
          <div className="md:col-span-2">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">搜尋客戶</label>
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="姓名、電話、地址..."
                className="w-full pl-9 pr-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:ring-2 focus:ring-red-500 outline-none text-sm bg-white dark:bg-gray-800 dark:text-white"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button onClick={() => setSearchTerm('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  <X size={14} />
                </button>
              )}
            </div>
          </div>
          <div className="md:col-span-1">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">標籤篩選</label>
            <select
              className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:ring-2 focus:ring-red-500 outline-none text-sm bg-white dark:bg-gray-800 dark:text-white"
              value={tagFilter}
              onChange={e => setTagFilter(e.target.value)}
            >
              <option value="">全部標籤</option>
              <optgroup label="自動標籤">
                {autoTagRules.filter(r => r.is_active === 1 || r.is_active === true).map(r => (
                  <option key={r.key} value={r.key}>{r.label}</option>
                ))}
              </optgroup>
              {allTags.length > 0 && (
                <optgroup label="手動標籤">
                  {allTags.map(tag => (
                    <option key={tag} value={tag}>{tag}</option>
                  ))}
                </optgroup>
              )}
            </select>
          </div>
          <div className="md:col-span-1">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">排序</label>
            <select
              className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:ring-2 focus:ring-red-500 outline-none text-sm bg-white dark:bg-gray-800 dark:text-white"
              value={sortBy}
              onChange={e => setSortBy(e.target.value as any)}
            >
              <option value="lastOrder">最後下單</option>
              <option value="orderCount">下單次數</option>
              <option value="totalSpent">累計消費</option>
            </select>
          </div>
          <div className="md:col-span-2 flex items-center justify-end gap-3">
            <button
              onClick={() => setSortDesc(!sortDesc)}
              className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1 px-2 py-1 border border-gray-200 rounded hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700"
            >
              {sortDesc ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
              {sortDesc ? '降序' : '升序'}
            </button>
            <button
              onClick={downloadCSV}
              className="text-xs text-gray-600 dark:text-gray-300 flex items-center gap-1 px-2 py-1 border border-gray-200 rounded hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700"
            >
              <Download size={14} /> 下載 CSV
            </button>
            <button
              onClick={() => setShowImportModal(true)}
              className="text-xs text-red-700 dark:text-red-300 flex items-center gap-1 px-2 py-1 border border-red-200 rounded hover:bg-red-50 dark:border-red-900/50 dark:hover:bg-red-900/20"
            >
              <FileUp size={14} /> 導入聯繫人
            </button>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              共 <span className="font-bold text-gray-800 dark:text-white">{sortedCustomers.length}</span> 位客戶
            </div>
          </div>
        </div>
      </div>

      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 flex justify-between items-center dark:bg-green-900/20 dark:border-green-800 dark:text-green-300">
          <span>{success}</span>
          <button onClick={() => setSuccess(null)} className="text-green-700 hover:text-green-900 dark:text-green-300 dark:hover:text-green-100"><X className="w-5 h-5" /></button>
        </div>
      )}

      {/* Customers Table */}
      <div className="overflow-x-auto bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
        <table className="min-w-full">
          <thead>
            <tr className="bg-gray-100 dark:bg-gray-900 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
              <th className="p-4">客戶</th>
              <th className="p-4">電話</th>
              <th className="p-4">來源</th>
              <th className="p-4">常用地址</th>
              <th className="p-4 text-center">下單次數</th>
              <th className="p-4 text-right">累計消費</th>
              <th className="p-4">最後下單</th>
              <th className="p-4">標籤</th>
              <th className="p-4">狀態</th>
              <th className="p-4">操作</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {loading ? (
              <tr>
                <td colSpan={10} className="p-8 text-center text-gray-400">加載中...</td>
              </tr>
            ) : sortedCustomers.length === 0 ? (
              <tr>
                <td colSpan={10} className="p-8 text-center text-gray-500 dark:text-gray-400">
                  {customers.length === 0 ? '暫無客戶數據' : '沒有找到相符的客戶'}
                </td>
              </tr>
            ) : (
              sortedCustomers.map(customer => {
                const autoTags = evaluateAutoTags(customer, autoTagRules);
                return (
                  <tr key={customer.phone} className={`hover:bg-red-50 dark:hover:bg-gray-700 transition-colors ${customer.isUnsubscribed ? 'bg-red-50 dark:bg-red-900/10' : ''}`}>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-red-100 dark:bg-red-900 flex items-center justify-center text-red-600 dark:text-red-400 text-sm font-bold">
                          {customer.name?.charAt(0) || '?'}
                        </div>
                        <span className="text-sm font-medium text-gray-800 dark:text-gray-300">{customer.name}</span>
                      </div>
                    </td>
                    <td className="p-4 text-sm text-gray-600 dark:text-gray-400 font-mono">{customer.phone}</td>
                    <td className="p-4">
                      {customer.source === 'imported' ? (
                        <span className="inline-flex items-center px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-[10px] font-medium dark:bg-blue-900/20 dark:text-blue-300">導入</span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 bg-red-100 text-red-700 rounded text-[10px] font-medium dark:bg-red-900/20 dark:text-red-300">訂單</span>
                      )}
                    </td>
                    <td className="p-4 text-sm text-gray-600 dark:text-gray-400 max-w-xs truncate">
                      {customer.estate || customer.address || '-'}
                    </td>
                    <td className="p-4 text-center">
                      <span className="inline-flex items-center gap-1 text-sm font-bold text-gray-800 dark:text-white">
                        <ShoppingBag size={14} className="text-red-500" />
                        {customer.orderCount}
                      </span>
                    </td>
                    <td className="p-4 text-right text-sm font-bold text-gray-800 dark:text-white">
                      <span className="flex items-center justify-end gap-1">
                        <DollarSign size={14} className="text-green-500" />
                        {formatPrice(customer.totalSpent)}
                      </span>
                    </td>
                    <td className="p-4 text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">
                      {customer.lastOrderAt
                        ? new Date(customer.lastOrderAt * 1000).toLocaleDateString('zh-HK')
                        : '-'}
                    </td>
                    <td className="p-4">
                      <div className="flex flex-wrap gap-1">
                        {autoTags.map(t => (
                          <span key={t.key} title={t.explanation || ''} className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium border cursor-help ${t.color}`}>
                            {t.label}
                          </span>
                        ))}
                        {customer.manualTags.map(t => (
                          <span key={t.id} className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium border ${TAG_COLORS[t.color] || TAG_COLORS.gray}`}>
                            {t.tag}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="p-4">
                      {customer.isUnsubscribed ? (
                        <span className="inline-flex items-center px-2 py-0.5 bg-red-100 text-red-700 rounded text-[10px] font-medium dark:bg-red-900/20 dark:text-red-300" title={customer.unsubscribeRemark || ''}>
                          拒收
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 bg-green-100 text-green-700 rounded text-[10px] font-medium dark:bg-green-900/20 dark:text-green-300">可發送</span>
                      )}
                    </td>
                    <td className="p-4 space-x-2 whitespace-nowrap">
                      <button
                        onClick={() => openEditModal(customer)}
                        className="bg-gray-600 text-white px-2 py-1.5 rounded text-xs hover:bg-gray-700 transition-colors inline-flex items-center gap-1"
                        title="編輯"
                      >
                        <Edit size={12} /> 編輯
                      </button>
                      <button
                        onClick={() => openTagModal(customer)}
                        className="bg-purple-500 text-white px-2 py-1.5 rounded text-xs hover:bg-purple-600 transition-colors inline-flex items-center gap-1"
                      >
                        <Tag size={12} /> 標籤
                      </button>
                      {!customer.isUnsubscribed && (
                        <button
                          onClick={() => setUnsubscribeCustomer(customer)}
                          className="bg-red-500 text-white px-2 py-1.5 rounded text-xs hover:bg-red-600 transition-colors inline-flex items-center gap-1"
                        >
                          <AlertCircle size={12} /> 拒收
                        </button>
                      )}
                      {customer.source === 'imported' && (
                        <button
                          onClick={() => handleDeleteCustomer(customer)}
                          className="bg-red-700 text-white px-2 py-1.5 rounded text-xs hover:bg-red-800 transition-colors inline-flex items-center gap-1"
                          title="刪除"
                        >
                          <Trash2 size={12} /> 刪除
                        </button>
                      )}
                      {customer.orderCount > 0 && (
                        <button
                          onClick={() => viewCustomerOrders(customer.phone)}
                          className="bg-blue-500 text-white px-2 py-1.5 rounded text-xs hover:bg-blue-600 transition-colors inline-flex"
                        >
                          訂單
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Tag Manager Modal */}
      {showTagModal && selectedCustomer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-lg w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <TagManager
                phone={selectedCustomer.phone}
                name={selectedCustomer.name}
                orderCount={selectedCustomer.orderCount}
                totalSpent={selectedCustomer.totalSpent}
                manualTags={selectedCustomer.manualTags}
                onTagsChange={() => {
                  fetchCustomers();
                }}
              />
              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowTagModal(false)}
                  className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm hover:bg-gray-300 dark:hover:bg-gray-600 transition"
                >
                  關閉
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingCustomer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-6">
            <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">編輯客戶</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">姓名</label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm dark:bg-gray-800 dark:text-white dark:border-gray-600"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  電話
                  {editingCustomer.source !== 'imported' && (
                    <span className="text-xs text-gray-400 ml-1">（有訂單記錄的客戶不能修改電話）</span>
                  )}
                </label>
                <input
                  type="text"
                  value={editForm.phone}
                  disabled={editingCustomer.source !== 'imported'}
                  onChange={e => setEditForm(f => ({ ...f, phone: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm dark:bg-gray-800 dark:text-white dark:border-gray-600 disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-700"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">地址</label>
                <input
                  type="text"
                  value={editForm.address}
                  onChange={e => setEditForm(f => ({ ...f, address: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm dark:bg-gray-800 dark:text-white dark:border-gray-600"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">屋苑</label>
                <input
                  type="text"
                  value={editForm.estate}
                  onChange={e => setEditForm(f => ({ ...f, estate: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm dark:bg-gray-800 dark:text-white dark:border-gray-600"
                />
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <button onClick={() => setEditingCustomer(null)} className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600">取消</button>
              <button onClick={handleEditSave} disabled={isSubmittingEdit} className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50">
                {isSubmittingEdit ? '儲存中...' : '儲存'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Unsubscribe Modal */}
      {unsubscribeCustomer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-6">
            <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">標記拒收</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              將 <span className="font-medium text-gray-900 dark:text-white">{unsubscribeCustomer.name || unsubscribeCustomer.phone}</span>（{unsubscribeCustomer.phone}）標記為拒收廣播。
              標記後會自動加上「拒收」標籤，後續廣播將不會發送給此客戶。
            </p>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">備註（可選）</label>
            <textarea
              rows={3}
              value={unsubscribeRemark}
              onChange={e => setUnsubscribeRemark(e.target.value)}
              placeholder="例如：客人於 WhatsApp 回覆要求取消"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm dark:bg-gray-800 dark:text-white dark:border-gray-600"
            />
            <div className="flex gap-2 mt-4">
              <button onClick={() => { setUnsubscribeCustomer(null); setUnsubscribeRemark(''); }} className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600">取消</button>
              <button onClick={handleUnsubscribe} className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">確認拒收</button>
            </div>
          </div>
        </div>
      )}

      {/* Import Contacts Modal */}
      {showImportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-lg w-full p-6">
            <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">導入聯繫人</h2>
            <div className="flex gap-2 mb-4">
              <button onClick={() => setImportFormat('csv')} className={`px-3 py-1 rounded text-sm ${importFormat === 'csv' ? 'bg-red-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}><FileUp className="w-3 h-3 inline mr-1" />CSV</button>
              <button onClick={() => setImportFormat('json')} className={`px-3 py-1 rounded text-sm ${importFormat === 'json' ? 'bg-red-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}><FileJson className="w-3 h-3 inline mr-1" />JSON</button>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              {importFormat === 'csv' ? '格式：姓名,電話（每行一個，第一行可為標題）' : '格式：[{"name":"","phone":""},...]'}
            </p>
            <textarea
              rows={8}
              value={importText}
              onChange={e => setImportText(e.target.value)}
              placeholder={importFormat === 'csv' ? '陳大文,85291234567\n李小明,85292345678' : '[{"name":"陳大文","phone":"85291234567"}]'}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm dark:bg-gray-800 dark:text-white dark:border-gray-600"
            />
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                <Tag className="w-4 h-4 inline mr-1 text-red-600" />
                標籤（多個用逗號分隔，可選）
              </label>
              <input
                type="text"
                value={importTags}
                onChange={e => setImportTags(e.target.value)}
                placeholder="例如：2026夏季推廣, 潛在客戶"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm dark:bg-gray-800 dark:text-white dark:border-gray-600"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">導入後會自動為這批聯繫人打上這些標籤，之後可用「指定標籤」發送廣播。</p>
            </div>
            <div className="flex gap-2 mt-6">
              <button onClick={() => { setShowImportModal(false); setImportText(''); setImportTags(''); }} className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600">取消</button>
              <button
                onClick={handleImport}
                disabled={isImporting}
                className="flex-1 flex items-center justify-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isImporting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                導入
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminCustomers;
